import { useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Divider, IconButton, MenuItem,
  Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  stockApi, stockWriteOffsApi, warehousesApi,
  type ItemDto, type StockAvailabilityDto, type WriteOffReason,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { ItemPicker } from "@/components/Pickers";

const REASONS: WriteOffReason[] = ["Expired", "Damaged", "Lost", "QcReject"];

interface DraftLine {
  key: string; item: ItemDto | null;
  batches: StockAvailabilityDto[]; batchId: string;
  qty: number; reasonCode: WriteOffReason; lineNotes: string;
}
const blank = (): DraftLine => ({
  key: crypto.randomUUID(), item: null, batches: [], batchId: "",
  qty: 0, reasonCode: "Expired", lineNotes: "",
});

/**
 * UC-M1-INV-05 — Create a stock write-off. Lines pick a specific batch from the
 * chosen warehouse; posting writes one WRITE_OFF ledger row per line at batch cost.
 */
export function CreateStockWriteOffPage() {
  const navigate = useNavigate();
  const [warehouseId, setWarehouseId] = useState("");
  const [documentDate, setDocumentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([blank()]);

  const warehouses = useQuery({
    queryKey: ["warehouses", "all"],
    queryFn: () => warehousesApi.list({ pageSize: 200 }),
  });

  const update = (key: string, patch: Partial<DraftLine>) =>
    setLines(ls => ls.map(l => l.key === key ? { ...l, ...patch } : l));
  const remove = (key: string) =>
    setLines(ls => ls.length === 1 ? ls : ls.filter(l => l.key !== key));

  const onItemChange = async (key: string, item: ItemDto | null) => {
    if (!item || !warehouseId) {
      update(key, { item, batches: [], batchId: "" });
      return;
    }
    const batches = await stockApi.availability({ itemId: item.id, warehouseId });
    update(key, { item, batches, batchId: batches[0]?.batchId ?? "" });
  };

  const validate = (): string | null => {
    if (!warehouseId) return "Pick a warehouse.";
    for (const [i, l] of lines.entries()) {
      if (!l.item) return `Line ${i + 1}: pick an item.`;
      if (!l.batchId) return `Line ${i + 1}: pick a batch.`;
      if (l.qty <= 0) return `Line ${i + 1}: qty must be > 0.`;
      const batch = l.batches.find(b => b.batchId === l.batchId);
      if (batch && l.qty > batch.availableQty)
        return `Line ${i + 1}: only ${batch.availableQty} available.`;
    }
    return null;
  };

  const mutation = useMutation({
    meta: { successMessage: "Stock write-off created" },
    mutationFn: () => stockWriteOffsApi.create({
      warehouseId, documentDate,
      reason: reason || undefined, notes: notes || undefined,
      lines: lines.map(l => ({
        itemId: l.item!.id, batchId: l.batchId, qty: l.qty,
        reasonCode: l.reasonCode, lineNotes: l.lineNotes || undefined,
      })),
    }),
    onSuccess: (r) => navigate(`/inventory/writeoffs/${r.id}`),
  });

  const validationError = validate();

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New stock write-off</Typography>
      <Paper sx={{ p: 3 }}>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
        <Alert severity="warning" sx={{ mb: 2 }}>
          Posting writes a negative ledger row per line at batch cost. Use for expired stock,
          damaged units, lost inventory or QC rejections — corrections require a positive
          adjustment after the fact.
        </Alert>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Warehouse *" size="small" sx={{ minWidth: 280 }}
            value={warehouseId} onChange={(e) => { setWarehouseId(e.target.value); setLines([blank()]); }}>
            <MenuItem value="">— pick warehouse —</MenuItem>
            {warehouses.data?.items.map(w => (
              <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>
            ))}
          </TextField>
          <TextField type="date" label="Document date *" size="small" InputLabelProps={{ shrink: true }}
            value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} />
          <TextField label="Header reason" size="small" sx={{ flex: 1, minWidth: 240 }}
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. monthly expiry sweep" />
        </Stack>
        <TextField label="Notes" fullWidth multiline minRows={1}
          value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ mb: 2 }} />

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Lines</Typography>
          <Button startIcon={<AddIcon />} size="small"
            onClick={() => setLines(ls => [...ls, blank()])} disabled={!warehouseId}>Add line</Button>
        </Stack>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ minWidth: 280 }}>Item *</TableCell>
            <TableCell sx={{ minWidth: 240 }}>Batch *</TableCell>
            <TableCell align="right" sx={{ width: 110 }}>Qty *</TableCell>
            <TableCell sx={{ width: 160 }}>Reason *</TableCell>
            <TableCell>Line note</TableCell>
            <TableCell sx={{ width: 50 }} />
          </TableRow></TableHead>
          <TableBody>
            {lines.map((l, idx) => {
              const batch = l.batches.find(b => b.batchId === l.batchId);
              return (
                <TableRow key={l.key}>
                  <TableCell>
                    <ItemPicker value={l.item} onChange={(v) => onItemChange(l.key, v)}
                      label={`Line ${idx + 1}`} required error={!l.item} />
                  </TableCell>
                  <TableCell>
                    <TextField select size="small" fullWidth value={l.batchId}
                      onChange={(e) => update(l.key, { batchId: e.target.value })}
                      disabled={l.batches.length === 0}>
                      <MenuItem value="">— pick batch —</MenuItem>
                      {l.batches.map(b => (
                        <MenuItem key={b.batchId} value={b.batchId}>
                          {b.batchNo}{b.expiryDate ? ` · exp ${new Date(b.expiryDate).toLocaleDateString()}` : ""} · avail {b.availableQty}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" sx={{ width: 100 }}
                      value={l.qty} onChange={(e) => update(l.key, { qty: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: "0.001", style: { textAlign: "right" } }}
                      helperText={batch ? `avail ${batch.availableQty}` : ""} />
                  </TableCell>
                  <TableCell>
                    <TextField select size="small" fullWidth value={l.reasonCode}
                      onChange={(e) => update(l.key, { reasonCode: e.target.value as WriteOffReason })}>
                      {REASONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth value={l.lineNotes}
                      onChange={(e) => update(l.key, { lineNotes: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => remove(l.key)} disabled={lines.length === 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
          <Button onClick={() => navigate("/inventory/writeoffs")}>Cancel</Button>
          <Button variant="contained" disabled={!!validationError || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => mutation.mutate()}>Save draft write-off</Button>
        </Stack>
        {validationError && <Alert severity="info" sx={{ mt: 2 }}>{validationError}</Alert>}
      </Paper>
    </Box>
  );
}
